import { Checkbox as RadixCheckbox } from "radix-ui";
import { type ReactNode, useId } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export interface CheckboxProps {
	checked?: boolean;
	defaultChecked?: boolean;
	onCheckedChange?: (checked: boolean) => void;
	/** Подпись может содержать ссылки — согласия в онбординге устроены именно так. */
	label?: ReactNode;
	disabled?: boolean;
	required?: boolean;
	name?: string;
	value?: string;
	id?: string;
	className?: string;
}

/**
 * Квадрат 20px r6. Зелёный только у включённого состояния; выключенное живёт
 * на серой заливке с обводкой — так пустой чекбокс виден на белой карточке,
 * где рамок больше нигде нет.
 *
 * Чекбокс — только для мультивыбора и согласий. Любой бинарный тумблер
 * («присылать письма») — Switch.
 */
export function Checkbox({
	checked,
	defaultChecked,
	onCheckedChange,
	label,
	disabled,
	required,
	name,
	value,
	id,
	className,
}: CheckboxProps) {
	const autoId = useId();
	const boxId = id ?? autoId;
	return (
		// `group` — чтобы ховер на подписи подсвечивал и подпись, и квадрат:
		// кликается вся строка целиком, значит и реагировать она обязана целиком.
		<div className={cn("group flex items-start gap-2.5", className)}>
			<RadixCheckbox.Root
				id={boxId}
				checked={checked}
				defaultChecked={defaultChecked}
				onCheckedChange={(next) => onCheckedChange?.(next === true)}
				disabled={disabled}
				required={required}
				name={name}
				value={value}
				className={cn(
					"inline-flex size-5 shrink-0 items-center justify-center rounded-sm text-fg",
					"bg-sunken shadow-[inset_0_0_0_1px_var(--color-line)]",
					"transition-colors duration-150 ease-smooth",
					"data-[state=checked]:bg-brand data-[state=checked]:shadow-none",
					"focus-ring cursor-pointer",
					// Ховер по всей строке: заливка невыбранного квадрата темнеет
					// (правило дизайна — ховер только затемняет).
					"group-hover:data-[state=unchecked]:bg-btn-secondary-hover",
					"disabled:cursor-not-allowed disabled:opacity-60",
					// Подпись занимает две строки — квадрат прижимается к первой.
					label ? "mt-0.5" : "",
				)}
			>
				<RadixCheckbox.Indicator
					style={{ animation: "iec-badge-in var(--dur-react) var(--ease-spring)" }}
				>
					<Icon name="Check" size={14} strokeWidth={2.5} />
				</RadixCheckbox.Indicator>
			</RadixCheckbox.Root>
			{label != null ? (
				<label
					htmlFor={boxId}
					className={cn(
						"t-body select-none transition-colors duration-150 ease-smooth",
						disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer group-hover:text-fg-muted",
					)}
				>
					{label}
				</label>
			) : null}
		</div>
	);
}
